import React from "react";
import { useTranslation } from "react-i18next";

// Region numbering matches BodySurfaceArea.jsx (front 1-17, back 18-33) and the
// `answer` field returned by fetch_bsa_details, since both are backed by the
// same log_bsa selectedStates the user saved.
const INACTIVE_FILL = "#a9f3ef";
const ACTIVE_FILL = "#FF9898";
const ACTIVE_STROKE = "#D8191F";

const SelfStatsAffectedSkin = ({ selectedStates, score, logDate }) => {
  const { t } = useTranslation();
  const isActive = (pathIndex) =>
    Array.isArray(selectedStates) && selectedStates.some((s) => Number(s.answer) === pathIndex);

  const p = (idx) => ({
    fill: isActive(idx) ? ACTIVE_FILL : INACTIVE_FILL,
    stroke: isActive(idx) ? ACTIVE_STROKE : "none",
    strokeWidth: isActive(idx) ? "1px" : "0",
  });

  const pct = (idx, x, y, label) =>
    isActive(idx) ? (
      <text x={x} y={y} textAnchor="middle" dominantBaseline="middle" fill="#333" fontSize="7px" fontWeight="600">
        {label}
      </text>
    ) : null;

  return (
    <div className="selfstats-box affected-skin-box">
      <h2 className="selfstats-title">{t("selfStats.bsaPercentage")}</h2>

      <div className="affected-skin-row">
        <div className="affected-skin-col">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 140.139 335.509" height="60vh">
            {/* Head */}
            <g>
              <path
                d="M129.4,17.723C128.588,7.769,121.772,0,113.483,0c-8.211,0-14.974,7.627-15.89,17.447-.037,0-.073-.009-.11-.009-1.371,0-2.483,1.827-2.483,4.081S96.112,25.6,97.483,25.6a1.6,1.6,0,0,0,.685-.161c1.987,8.107,8.091,14.01,15.315,14.01,7.128,0,13.164-5.747,15.234-13.686a1.568,1.568,0,0,0,.57.113c1.371,0,2.483-1.827,2.483-4.081,0-2.19-1.05-3.972-2.369-4.071"
                transform="translate(-42.586 0.725)"
                {...p(1)}
              />
              {pct(1, "50%", "7%", "4.50%")}
            </g>
            {/* Upper trunk */}
            <g>
              <path
                d="M81,85.69v28.144c2.724.491,6.721,1.138,11.586,1.65a123.513,123.513,0,0,0,24.828.552,109.7,109.7,0,0,0,14.345-2.207V86.242l-17.1-8.828A6.247,6.247,0,0,1,113,73.552a16.384,16.384,0,0,1-4.966,1.1A16.752,16.752,0,0,1,99.759,73a4.528,4.528,0,0,1-.552,2.207,4.626,4.626,0,0,1-1.655,1.655C95.976,77.846,89.666,81.25,81,85.69"
                transform="translate(-36.31 -32.724)"
                {...p(2)}
              />
              {pct(2, "50%", "20%", "6%")}
            </g>
            {/* Abdomen */}
            <g>
              <path
                d="M81,149a116.756,116.756,0,0,0,12.69,2.207,111.533,111.533,0,0,0,20.966.552,102.763,102.763,0,0,0,17.1-2.759L130.1,188.173a55.106,55.106,0,0,1-7.724,2.759c-14.087,3.875-26.175,1.13-30.9,0a68.318,68.318,0,0,1-8.828-2.759"
                transform="translate(-36.31 -66.068)"
                {...p(3)}
              />
              {pct(3, "50%", "32%", "6%")}
            </g>
            {/* Lower abdomen */}
            <g>
              <path
                d="M129.518,247.4a142.188,142.188,0,0,1-1.655-14.345c-.161-3.525-.086-6.41,0-8.276-1.809.655-4.245,1.46-7.172,2.207A65.006,65.006,0,0,1,106.9,229.19a61.879,61.879,0,0,1-17.931-1.931,70.457,70.457,0,0,1-8.828-2.759c.025,1.944,0,4.924-.276,8.552-.341,4.419-.86,7.127-2.207,15.448-.39,2.408-.968,6.023-1.655,10.483a63.543,63.543,0,0,0,13.241,4.966c1.827.461,4.245,1.054,7.107,1.508a7.443,7.443,0,0,1,14.058.367,44.892,44.892,0,0,0,8.628-1.875,45.634,45.634,0,0,0,12.138-6.069c-.727-4.3-1.274-7.885-1.655-10.483"
                transform="translate(-34.069 -100.637)"
                {...p(4)}
              />
              {pct(4, "50%", "43%", "6%")}
            </g>
            {/* Genitalia */}
            <g>
              <path
                d="M126.586,298.793A5.793,5.793,0,1,1,120.793,293a5.793,5.793,0,0,1,5.793,5.793"
                transform="translate(-51.551 -131.343)"
                {...p(5)}
              />
              {pct(5, "50%", "50%", "1%")}
            </g>
            {/* Left upper arm */}
            <g>
              <path
                d="M66.186,96q-.118,11.322-.552,23.173-.42,11.281-1.1,22.069a10.507,10.507,0,0,1-9.931,1.1,10.486,10.486,0,0,1-2.759-1.655v-17.1a38.1,38.1,0,0,1,0-7.724,27.065,27.065,0,0,1,1.655-7.724,20.077,20.077,0,0,1,4.966-7.724A19.182,19.182,0,0,1,66.186,96"
                transform="translate(-23.151 -43.034)"
                {...p(6)}
              />
              {pct(6, "25%", "25%", "1.5%")}
            </g>
            {/* Left forearm */}
            <g>
              <path
                d="M34,211.449a21.354,21.354,0,0,1,1.655-7.724c.931-2.178,1.839-3.21,3.31-6.621.822-1.906,1.314-3.382,1.655-4.414A64.866,64.866,0,0,0,43.379,180a15.467,15.467,0,0,0,2.207,1.1,15.2,15.2,0,0,0,4.966,1.1,14.323,14.323,0,0,0,6.069-1.1c-.116,1.175-.3,2.891-.552,4.966a57.848,57.848,0,0,1-1.1,7.172c-.434,1.585-.9,2.743-1.655,5.517-.129.473-.33,1.231-.552,2.207-.077.338-.327,1.453-.552,2.759-.372,2.163-.5,3.86-.552,4.414A44.671,44.671,0,0,1,50,216.414a25.238,25.238,0,0,1-4.966,0,17.286,17.286,0,0,1-7.724-2.207A14.333,14.333,0,0,1,34,211.449"
                transform="translate(-15.241 -80.689)"
                {...p(7)}
              />
              {pct(7, "22%", "35%", "1.5%")}
            </g>
            {/* Left hand */}
            <g>
              <path
                d="M25,269.69a27.523,27.523,0,0,0,2.207-5.517,35.583,35.583,0,0,0,1.1-9.379c.091-4.012-.282-4.612,0-7.724a34.591,34.591,0,0,1,1.1-6.069,14.8,14.8,0,0,0,3.862,2.759A19.164,19.164,0,0,0,41,245.414a32.89,32.89,0,0,0,4.414,0,15.851,15.851,0,0,1-.552,4.414c-.52,1.9-1.052,2.41-2.759,6.069-1.135,2.433-1.015,2.454-2.207,4.966-1.222,2.574-1.559,3-2.207,4.414a29.712,29.712,0,0,0-2.207,7.172,12.915,12.915,0,0,1-3.31.552c-1.558.053-4.27.145-6.069-1.655A5.22,5.22,0,0,1,25,269.69"
                transform="translate(-11.207 -108.033)"
                {...p(8)}
              />
              <path
                d="M23.173,298.759a29.457,29.457,0,0,1-3.862,0,9.612,9.612,0,0,1-3.31-.552A6.743,6.743,0,0,1,13.242,296a13.761,13.761,0,0,1-1.655,2.207,24.023,24.023,0,0,1-3.31,2.759,32.476,32.476,0,0,0-3.31,2.759,19.542,19.542,0,0,0-2.759,3.862A33.164,33.164,0,0,0,0,312v1.1a1.014,1.014,0,0,0,.552.552.911.911,0,0,0,.552,0c1.017-.284,2.207-2.207,2.207-2.207,1.526-2.467,1.624-2.805,2.207-3.31a5.215,5.215,0,0,1,2.207-1.1,21.047,21.047,0,0,0-1.1,2.759,33.905,33.905,0,0,0-1.1,6.069c-.657,4.945-.276,3.64-.552,4.966,0,0-.651,3.133,0,4.414a1.058,1.058,0,0,0,.552.552.859.859,0,0,0,.552,0,.938.938,0,0,0,.552-.552,9.234,9.234,0,0,0,0,2.207c.145,1.187.426,1.524.552,1.655a1.59,1.59,0,0,0,1.1.552,1.73,1.73,0,0,0,1.1-.552,10.282,10.282,0,0,0,1.655-1.655c1.528-1.869,2.818-3.553,3.862-4.966,1.058-1.43.578-.843,3.862-5.517a22.932,22.932,0,0,0,2.759-4.414,17.236,17.236,0,0,0,1.1-3.862,41.643,41.643,0,0,0,.552-9.931"
                transform="translate(0 -132.688)"
                {...p(8)}
              />
              {pct(8, "17%", "44%", "1.5%")}
            </g>
            {/* Right upper arm */}
            <g>
              <path
                d="M176,97.713q.118,11.322.552,23.173.42,11.281,1.1,22.069a10.507,10.507,0,0,0,9.931,1.1,10.486,10.486,0,0,0,2.759-1.655V125.3a38.1,38.1,0,0,0,0-7.724,27.063,27.063,0,0,0-1.655-7.724,20.078,20.078,0,0,0-4.966-7.724A19.182,19.182,0,0,0,176,97.713"
                transform="translate(-78.896 -43.802)"
                {...p(9)}
              />
              {pct(9, "75%", "25%", "1.5%")}
            </g>
            {/* Right forearm */}
            <g>
              <path
                d="M201.621,213.162a21.354,21.354,0,0,0-1.655-7.724c-.931-2.178-1.839-3.21-3.31-6.621-.822-1.906-1.314-3.382-1.655-4.414a64.865,64.865,0,0,1-2.759-12.69,15.468,15.468,0,0,1-2.207,1.1,15.2,15.2,0,0,1-4.966,1.1,14.323,14.323,0,0,1-6.069-1.1c.116,1.175.3,2.891.552,4.966a57.849,57.849,0,0,0,1.1,7.172c.434,1.585.9,2.743,1.655,5.517.129.473.33,1.231.552,2.207.077.338.327,1.453.552,2.759.372,2.163.5,3.86.552,4.414a44.671,44.671,0,0,0,1.655,8.276,25.238,25.238,0,0,0,4.966,0,17.286,17.286,0,0,0,7.724-2.207,14.335,14.335,0,0,0,3.31-2.759"
                transform="translate(-80.24 -81.457)"
                {...p(10)}
              />
              {pct(10, "78%", "35%", "1.5%")}
            </g>
            {/* Right hand */}
            <g>
              <path
                d="M212.413,271.4a27.525,27.525,0,0,1-2.207-5.517,35.584,35.584,0,0,1-1.1-9.379c-.091-4.012.282-4.613,0-7.724a34.59,34.59,0,0,0-1.1-6.069,14.8,14.8,0,0,1-3.862,2.759,19.165,19.165,0,0,1-7.724,1.655,32.9,32.9,0,0,1-4.414,0,15.847,15.847,0,0,0,.552,4.414c.52,1.9,1.052,2.411,2.759,6.069,1.135,2.433,1.015,2.453,2.207,4.966,1.221,2.574,1.559,3,2.207,4.414a29.709,29.709,0,0,1,2.207,7.173,12.914,12.914,0,0,0,3.31.552c1.558.053,4.27.145,6.069-1.655a5.22,5.22,0,0,0,1.1-1.655"
                transform="translate(-86.067 -108.801)"
                {...p(11)}
              />
              <path
                d="M211.938,300.472a29.473,29.473,0,0,0,3.862,0,9.614,9.614,0,0,0,3.31-.552,6.744,6.744,0,0,0,2.759-2.207,13.776,13.776,0,0,0,1.655,2.207,24.021,24.021,0,0,0,3.31,2.759,32.453,32.453,0,0,1,3.31,2.759A19.534,19.534,0,0,1,232.9,309.3a33.169,33.169,0,0,1,2.207,4.414v1.1a1.013,1.013,0,0,1-.552.552.911.911,0,0,1-.552,0c-1.017-.284-2.207-2.207-2.207-2.207-1.526-2.467-1.624-2.805-2.207-3.31a5.217,5.217,0,0,0-2.207-1.1,21.066,21.066,0,0,1,1.1,2.759,33.908,33.908,0,0,1,1.1,6.069c.657,4.945.276,3.64.552,4.966,0,0,.651,3.133,0,4.414a1.059,1.059,0,0,1-.552.552.858.858,0,0,1-.552,0,.938.938,0,0,1-.552-.552,9.236,9.236,0,0,1,0,2.207c-.145,1.187-.426,1.524-.552,1.655a1.59,1.59,0,0,1-1.1.552,1.731,1.731,0,0,1-1.1-.552,10.284,10.284,0,0,1-1.655-1.655c-1.528-1.869-2.818-3.553-3.862-4.965-1.058-1.43-.578-.843-3.862-5.517a22.926,22.926,0,0,1-2.759-4.414,17.228,17.228,0,0,1-1.1-3.862,41.644,41.644,0,0,1-.552-9.931"
                transform="translate(-94.971 -133.456)"
                {...p(11)}
              />
              {pct(11, "82%", "44%", "1.5%")}
            </g>
            {/* Left thigh */}
            <g>
              <path
                d="M100.139,304.683a7.375,7.375,0,0,1-5.632-8.219A47.8,47.8,0,0,1,74.02,290c-.429,3.036-.9,7.188-1.1,12.138a132.507,132.507,0,0,0,1.1,22.621c.519,4.259,1.352,9.709,2.759,16a58.37,58.37,0,0,0,23.173,1.1,233.071,233.071,0,0,0,.552-31.449c-.1-1.944-.223-3.849-.364-5.731"
                transform="translate(-32.64 -129.999)"
                {...p(12)}
              />
              {pct(12, "39%", "56%", "3%")}
            </g>
            {/* Left lower leg */}
            <g>
              <path
                d="M82.1,440.07a41.376,41.376,0,0,1-.552-11.586c.249-2.577.9-5.764,2.207-12.138a58.357,58.357,0,0,0,1.1-6.069,45.364,45.364,0,0,0,0-8.828A55.055,55.055,0,0,0,81,386a48.128,48.128,0,0,0,6.069,1.1,53.642,53.642,0,0,0,11.586,0c1.846-.169,3.368-.385,4.414-.552.118,1.872.319,5.164.552,9.379.394,7.149.43,8.848-.552,11.035a21.189,21.189,0,0,0-1.655,3.862,16.658,16.658,0,0,0-.552,5.517c.276,5.613,1.072,10.83,1.1,11.034a47.618,47.618,0,0,1,0,13.793,45.45,45.45,0,0,1-19.862-1.1"
                transform="translate(-36.31 -173.033)"
                {...p(13)}
              />
              {pct(13, "40%", "72%", "3%")}
            </g>
            {/* Left foot */}
            <g>
              <path
                d="M84,487a39.39,39.39,0,0,0,18.759,1.1c-.16,1.641-.364,3.912-.552,6.621-.21,3.022-.534,8.526-.552,16-.022,8.889.411,11.991,1.1,14.9.31,1.3.7,2.584,1.1,3.862a8.616,8.616,0,0,1,.552,2.207,11.189,11.189,0,0,1-.552,3.31c-.141.626-.343,1.575-.552,2.759a25.68,25.68,0,0,0,.552,8.828,4.941,4.941,0,0,1,0,3.31,4.756,4.756,0,0,1-2.207,2.207,8.756,8.756,0,0,1-6.069.552c-1.652-.3-4.872-.893-6.069-3.31a6.056,6.056,0,0,1-.552-2.207,6.215,6.215,0,0,1,.552-2.759,68.539,68.539,0,0,1,4.966-9.931,6.4,6.4,0,0,0,1.1-2.759,6.317,6.317,0,0,0-.552-3.31c-2.193-5.079-2.7-10.74-4.414-16-3.891-11.931-4.309-13.135-4.966-16A93.859,93.859,0,0,1,84,487"
                transform="translate(-37.655 -218.308)"
                {...p(14)}
              />
              {pct(14, "40%", "84%", "3%")}
            </g>
            {/* Right thigh */}
            <g>
              <path
                d="M157.565,300.149A70.078,70.078,0,0,0,155.91,289a44.273,44.273,0,0,1-9.379,5.018,38.731,38.731,0,0,1-11.6,2.713c0,.09.014.178.014.269a7.445,7.445,0,0,1-4.992,7.025c0,1.88.013,3.576.026,5.043.1,10.775.429,18.9.552,21.742.189,4.365.394,8.006.552,10.591a47.066,47.066,0,0,0,11.586,1.115,39.17,39.17,0,0,0,11.586-1.654c1.134-6.826,2.352-14.245,2.759-18.415a116.782,116.782,0,0,0,.552-22.3"
                transform="translate(-58.254 -129.55)"
                {...p(15)}
              />
              {pct(15, "62%", "56%", "3%")}
            </g>
            {/* Right lower leg */}
            <g>
              <path
                d="M153.489,441.315a41.376,41.376,0,0,0,.552-11.586c-.249-2.577-.9-5.764-2.207-12.138a58.339,58.339,0,0,1-1.1-6.069,45.363,45.363,0,0,1,0-8.828,55.056,55.056,0,0,1,3.862-15.448,48.132,48.132,0,0,1-6.069,1.1,53.642,53.642,0,0,1-11.586,0c-1.846-.169-3.368-.385-4.414-.552-.118,1.872-.319,5.164-.552,9.379-.394,7.149-.43,8.848.552,11.035a21.183,21.183,0,0,1,1.655,3.862,16.656,16.656,0,0,1,.552,5.517c-.276,5.613-1.072,10.83-1.1,11.035a47.618,47.618,0,0,0,0,13.793,45.449,45.449,0,0,0,19.862-1.1"
                transform="translate(-59.043 -173.591)"
                {...p(16)}
              />
              {pct(16, "60%", "72%", "3%")}
            </g>
            {/* Right foot */}
            <g>
              <path
                d="M153.705,488.57a39.391,39.391,0,0,1-18.759,1.1c.16,1.641.364,3.912.552,6.621.21,3.022.534,8.526.552,16,.022,8.889-.411,11.991-1.1,14.9-.31,1.3-.7,2.584-1.1,3.862a8.614,8.614,0,0,0-.552,2.207,11.188,11.188,0,0,0,.552,3.31c.141.626.343,1.575.552,2.759a25.679,25.679,0,0,1-.552,8.828,4.941,4.941,0,0,0,0,3.31,4.756,4.756,0,0,0,2.207,2.207,8.755,8.755,0,0,0,6.069.552c1.652-.3,4.872-.893,6.069-3.31a6.059,6.059,0,0,0,.552-2.207,6.214,6.214,0,0,0-.552-2.759,68.532,68.532,0,0,0-4.966-9.931,6.4,6.4,0,0,1-1.1-2.759,6.316,6.316,0,0,1,.552-3.31c2.193-5.079,2.7-10.74,4.414-16,3.891-11.931,4.309-13.135,4.966-16a93.866,93.866,0,0,0,1.655-9.379"
                transform="translate(-59.746 -219.012)"
                {...p(17)}
              />
              {pct(17, "60%", "84%", "3%")}
            </g>
          </svg>
        </div>

        <div className="affected-skin-col">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 140.139 335.509" height="60vh">
            {/* Back head */}
            <g>
              <path
                d="M135.851,18.035C135.079,7.906,128.616,0,120.757,0c-7.786,0-14.2,7.761-15.067,17.754-.034,0-.069-.009-.1-.009-1.3,0-2.354,1.859-2.354,4.153s1.054,4.153,2.354,4.153a1.43,1.43,0,0,0,.649-.164c.992,4.34,2.423,7.3,5.161,9.85,1.365-3.752,5.406-5.666,9.385-5.666,4.106,0,8.013,1.931,9.385,5.666,2.651-2.485,4.02-5.341,5.035-9.521a1.406,1.406,0,0,0,.541.115c1.3,0,2.355-1.859,2.355-4.153,0-2.229-1-4.042-2.246-4.143"
                transform="translate(-50.927 0.725)"
                {...p(18)}
              />
              {pct(18, "50%", "7%", "4.50%")}
            </g>
            {/* Back upper trunk */}
            <g>
              <path
                d="M124.788,70.634a4.245,4.245,0,0,1-1.76-1.259,5.268,5.268,0,0,1-.834-1.638,9.279,9.279,0,0,0-18.421.866c-.033.047-.063.094-.1.142A4.2,4.2,0,0,1,101.912,70c-1.674.734-7.613,2.628-14.078,5.036-.586,16.367,0,23.292,0,33.994a75.248,75.248,0,0,0,8.8,2.518,87.1,87.1,0,0,0,15.837,1.889,72.713,72.713,0,0,0,16.424-1.259,70.666,70.666,0,0,0,9.385-2.518l.587-32.735-14.078-6.3"
                transform="translate(-43.203 -27.343)"
                {...p(19)}
              />
              {pct(19, "50%", "20%", "6%")}
            </g>
            {/* Back mid trunk */}
            <g>
              <path
                d="M87.573,155.782a110,110,0,0,0,12.742,2.378,104.348,104.348,0,0,0,21.052.594,96.662,96.662,0,0,0,17.174-2.973q-.831,21.107-1.662,42.213a52.838,52.838,0,0,1-7.756,2.973c-14.145,4.176-26.283,1.218-31.023,0A65.235,65.235,0,0,1,89.235,198"
                transform="translate(-43.202 -71.072)"
                {...p(20)}
              />
              {pct(20, "50%", "32%", "6%")}
            </g>
            {/* Back lower trunk */}
            <g>
              <path
                d="M85.193,238.217a55.2,55.2,0,0,0,7.625,2.908,57.9,57.9,0,0,0,16.424,2.326,65.088,65.088,0,0,0,16.424-2.326,67.448,67.448,0,0,0,7.039-2.326c.459,1.4,1.088,3.393,1.76,5.815a152.354,152.354,0,0,1,3.519,16.863,67.561,67.561,0,0,1,1.173,9.3,30.568,30.568,0,0,1-.587,8.14,15.446,15.446,0,0,1-1.76,5.233c-3.272,5.1-11.327,5.206-13.491,5.233a24.256,24.256,0,0,1-13.491-4.07A22.7,22.7,0,0,1,93.992,288.8c-2.076-.329-10.034-1.36-12.9-6.977-.841-1.645-.952-3.422-1.173-6.978a52.264,52.264,0,0,1,.586-10.466c.715-5.743,1.491-10.129,1.76-11.629.66-3.685,1.612-8.643,2.933-14.537"
                transform="translate(-39.388 -108.682)"
                {...p(21)}
              />
              {pct(21, "50%", "43%", "6%")}
            </g>
            {/* Back left upper arm */}
            <g>
              <path
                d="M70,91.524q-.12,12.363-.561,25.3-.427,12.318-1.123,24.1a10.423,10.423,0,0,1-3.929,1.807,10.122,10.122,0,0,1-6.175-.6,10.571,10.571,0,0,1-2.806-1.807V121.646a44.629,44.629,0,0,1,0-8.434,31.448,31.448,0,0,1,1.684-8.434,22.227,22.227,0,0,1,5.052-8.434A19.186,19.186,0,0,1,70,91.524"
                transform="translate(-27.235 -41.756)"
                {...p(22)}
              />
              {pct(22, "25%", "25%", "1.5%")}
            </g>
            {/* Back left forearm */}
            <g>
              <path
                d="M39.361,207.508a54.575,54.575,0,0,1,2.346-7.085c1.21-2.958,1.971-4.068,2.933-6.441a36.933,36.933,0,0,0,2.346-9.018,10.758,10.758,0,0,0,2.933,1.932c4.495,1.976,8.816-.052,9.972-.644a74.086,74.086,0,0,1-1.173,10.306,72.631,72.631,0,0,1-4.693,15.459,14.028,14.028,0,0,1-5.279.644,11.568,11.568,0,0,1-9.385-5.153"
                transform="translate(-19.418 -84.386)"
                {...p(23)}
              />
              {pct(23, "22%", "35%", "1.5%")}
            </g>
            {/* Back left hand */}
            <g>
              <path
                d="M24.312,269.672c.085-1.847.247-4.45.586-7.554.448-4.112.983-7.054,1.76-11.332.753-4.147,1.309-7.208,2.346-11.331.85-3.379,1.7-6.172,2.346-8.184a12.12,12.12,0,0,0,6.452,4.407,13.784,13.784,0,0,0,4.693.629,13.51,13.51,0,0,0,3.519-.629,69.023,69.023,0,0,1-1.173,6.924,76.971,76.971,0,0,1-4.106,12.59,100.536,100.536,0,0,1-8.212,15.738,7.544,7.544,0,0,1-4.106.63,7.8,7.8,0,0,1-4.106-1.889"
                transform="translate(-11.994 -105.513)"
                {...p(24)}
              />
              <path
                d="M19.878,306.506a23.574,23.574,0,0,1-3.313,0A7.736,7.736,0,0,1,13.725,306a5.894,5.894,0,0,1-2.367-2.032A12.684,12.684,0,0,1,9.939,306a21.023,21.023,0,0,1-2.84,2.539,28.4,28.4,0,0,0-2.839,2.54,18.025,18.025,0,0,0-2.367,3.556A31.469,31.469,0,0,0,0,318.7v1.016a.91.91,0,0,0,.473.508.734.734,0,0,0,.473,0c.873-.262,1.893-2.032,1.893-2.032,1.308-2.271,1.393-2.582,1.893-3.047a4.363,4.363,0,0,1,1.893-1.016,20.289,20.289,0,0,0-.946,2.54,33.28,33.28,0,0,0-.946,5.587c-.564,4.552-.237,3.351-.473,4.571,0,0-.559,2.884,0,4.064a.949.949,0,0,0,.473.508.694.694,0,0,0,.473,0,.843.843,0,0,0,.473-.508,9.128,9.128,0,0,0,0,2.032,2.592,2.592,0,0,0,.473,1.524,1.339,1.339,0,0,0,.946.508,1.446,1.446,0,0,0,.947-.508,9.2,9.2,0,0,0,1.42-1.524c1.31-1.721,2.417-3.271,3.313-4.572.907-1.316.5-.776,3.313-5.079a21.452,21.452,0,0,0,2.367-4.064,16.758,16.758,0,0,0,.946-3.555,41.1,41.1,0,0,0,.473-9.143"
                transform="translate(0 -138.679)"
                {...p(24)}
              />
              {pct(24, "17%", "44%", "1.5%")}
            </g>
            {/* Back right upper arm */}
            <g>
              <path
                d="M192.185,91.9q.12,12.363.561,25.3.427,12.318,1.123,24.1a10.423,10.423,0,0,0,3.929,1.807,10.122,10.122,0,0,0,6.175-.6,10.571,10.571,0,0,0,2.806-1.807V122.026a44.629,44.629,0,0,0,0-8.434,31.448,31.448,0,0,0-1.684-8.434,22.227,22.227,0,0,0-5.052-8.434,19.186,19.186,0,0,0-7.859-4.819"
                transform="translate(-94.81 -41.929)"
                {...p(25)}
              />
              {pct(25, "75%", "25%", "1.5%")}
            </g>
            {/* Back right forearm */}
            <g>
              <path
                d="M217.239,207.888a54.588,54.588,0,0,0-2.346-7.085c-1.21-2.958-1.971-4.068-2.933-6.441a36.956,36.956,0,0,1-2.346-9.018,10.749,10.749,0,0,1-2.933,1.932c-4.495,1.976-8.816-.052-9.972-.644a74.078,74.078,0,0,0,1.173,10.306,72.63,72.63,0,0,0,4.693,15.459,14.027,14.027,0,0,0,5.279.644,11.568,11.568,0,0,0,9.385-5.153"
                transform="translate(-97.042 -84.559)"
                {...p(26)}
              />
              {pct(26, "78%", "35%", "1.5%")}
            </g>
            {/* Back right hand */}
            <g>
              <path
                d="M231.146,270.051c-.085-1.847-.248-4.45-.587-7.554-.448-4.112-.983-7.054-1.76-11.332-.753-4.147-1.309-7.208-2.346-11.331-.85-3.379-1.7-6.172-2.346-8.184a12.121,12.121,0,0,1-6.452,4.407,13.783,13.783,0,0,1-4.692.629,13.515,13.515,0,0,1-3.52-.629,69.167,69.167,0,0,0,1.173,6.924,77,77,0,0,0,4.106,12.59,100.49,100.49,0,0,0,8.212,15.738,7.543,7.543,0,0,0,4.106.63,7.8,7.8,0,0,0,4.106-1.889"
                transform="translate(-103.324 -105.686)"
                {...p(27)}
              />
              <path
                d="M237.294,306.887a23.588,23.588,0,0,0,3.313,0,7.737,7.737,0,0,0,2.839-.508,5.9,5.9,0,0,0,2.367-2.032,12.686,12.686,0,0,0,1.42,2.032,21.029,21.029,0,0,0,2.84,2.539,28.4,28.4,0,0,1,2.839,2.54,18.026,18.026,0,0,1,2.367,3.556,31.471,31.471,0,0,1,1.893,4.063v1.016a.91.91,0,0,1-.473.508.735.735,0,0,1-.473,0c-.873-.262-1.893-2.032-1.893-2.032-1.308-2.271-1.393-2.582-1.893-3.047a4.363,4.363,0,0,0-1.893-1.016,20.287,20.287,0,0,1,.946,2.54,33.274,33.274,0,0,1,.946,5.587c.564,4.552.237,3.351.473,4.571,0,0,.559,2.884,0,4.064a.949.949,0,0,1-.473.508.694.694,0,0,1-.473,0,.844.844,0,0,1-.473-.508,9.127,9.127,0,0,1,0,2.032,2.591,2.591,0,0,1-.473,1.524,1.339,1.339,0,0,1-.946.508,1.447,1.447,0,0,1-.947-.508,9.2,9.2,0,0,1-1.42-1.524c-1.31-1.721-2.417-3.271-3.313-4.572-.907-1.316-.5-.776-3.313-5.079a21.453,21.453,0,0,1-2.367-4.064,16.758,16.758,0,0,1-.946-3.555,41.1,41.1,0,0,1-.473-9.143"
                transform="translate(-117.032 -138.853)"
                {...p(27)}
              />
              {pct(27, "82%", "44%", "1.5%")}
            </g>
            {/* Back left thigh */}
            <g>
              <path
                d="M79.9,321.674a20.3,20.3,0,0,0,4.106,3.777,20.975,20.975,0,0,0,7.625,3.148,23.017,23.017,0,0,0,8.8.629,22.11,22.11,0,0,0,6.452-1.889c.358,6.8.57,13.942.587,21.4.018,8.15-.2,15.927-.587,23.292a30.53,30.53,0,0,1-6.452,1.259,38.518,38.518,0,0,1-12.318-1.259c-1.981-.445-3.6-.916-4.693-1.259-1.107-8.006-1.845-14.843-2.346-20.145-.96-10.159-1.131-15.551-1.173-17-.141-4.869-.087-8.968,0-11.961"
                transform="translate(-39.374 -146.757)"
                {...p(28)}
              />
              {pct(28, "39%", "56%", "3%")}
            </g>
            {/* Back left lower leg */}
            <g>
              <path
                d="M89.262,457.234a38.872,38.872,0,0,1,.587-8.383c.546-2.981,1.042-3.58,1.76-7.685a29.39,29.39,0,0,0,.586-6.986,30.458,30.458,0,0,0-.586-4.191c-.616-3.4-1.181-5.115-2.346-9.78-.269-1.076-.685-2.771-1.173-4.89,1.11.386,2.722.907,4.692,1.4a34.636,34.636,0,0,0,11.145,1.4,27.513,27.513,0,0,0,7.039-1.4c.006,1.1.011,2.8,0,4.89,0,0-.036,6.583-.587,13.273a16.741,16.741,0,0,1-1.173,5.589,11.078,11.078,0,0,0-.587,1.4,12.383,12.383,0,0,0-.587,2.794,22.58,22.58,0,0,0,0,4.191c.265,3.629.681,4.216.587,6.287a14.726,14.726,0,0,1-.587,3.493,47.531,47.531,0,0,1-5.866.7,25.484,25.484,0,0,1-9.385-.7,21.3,21.3,0,0,1-3.519-1.4"
                transform="translate(-43.457 -189.481)"
                {...p(29)}
              />
              {pct(29, "40%", "72%", "3%")}
            </g>
            {/* Back left foot */}
            <g>
              <path
                d="M90.077,496.32a31.377,31.377,0,0,0,19.763,1.262,55.232,55.232,0,0,0-1.694,6.311c-.807,3.916-1.049,7.229-1.129,13.884-.118,9.743-.176,14.614.564,20.2a89.559,89.559,0,0,0,2.259,11.36c.528,1.058,1,2.012,1.412,2.84a7.116,7.116,0,0,1,.565,1.262,7.688,7.688,0,0,1,.564,3.155c-.109,1.95,0,1.893-.564,2.524-1.624,1.815-6.538,1.991-7.906,1.893-2.954-.211-5.825-.417-6.776-2.525a6.1,6.1,0,0,1,0-3.786c.331-1.524,1.05-2.014,2.259-4.417a11.259,11.259,0,0,0,1.129-2.525,8,8,0,0,0,0-3.156,27.643,27.643,0,0,0-1.694-6.311c-1.162-2.844-1.818-5.916-2.823-8.835-3.411-9.9-5.116-14.856-5.647-17.67a47.862,47.862,0,0,1-.282-15.462"
                transform="translate(-44.197 -226.436)"
                {...p(30)}
              />
              {pct(30, "40%", "84%", "3%")}
            </g>
            {/* Back right thigh */}
            <g>
              <path
                d="M141.309,374.149q-.068-8.387,0-17,.114-14.145.587-27.7a39.7,39.7,0,0,0,7.039,1.889c3.871.664,7.263,1.246,11.145,0A17.728,17.728,0,0,0,167.7,326.3c.185,1.688.416,4.068.587,6.925a152.9,152.9,0,0,1-.587,23.922c-.421,4.22-1.121,9.575-2.346,15.738a78.3,78.3,0,0,1-8.212,1.889c-4.348.754-7.326,1.245-11.145.629a23.878,23.878,0,0,1-4.693-1.259"
                transform="translate(-69.695 -148.87)"
                {...p(31)}
              />
              {pct(31, "62%", "56%", "3%")}
            </g>
            {/* Back right lower leg */}
            <g>
              <path
                d="M163.7,458.606a41.434,41.434,0,0,0-.587-8.657c-.546-3.078-1.042-3.7-1.76-7.935a31.326,31.326,0,0,1-.586-7.215,32.422,32.422,0,0,1,.586-4.328c.616-3.507,1.181-5.282,2.346-10.1.269-1.111.685-2.861,1.173-5.05-1.11.4-2.722.937-4.692,1.443a33.631,33.631,0,0,1-11.145,1.443A26.778,26.778,0,0,1,142,416.763c-.006,1.132-.011,2.893,0,5.05,0,0,.036,6.8.587,13.707a17.777,17.777,0,0,0,1.173,5.772,11.666,11.666,0,0,1,.587,1.443,13.156,13.156,0,0,1,.587,2.886,24.078,24.078,0,0,1,0,4.328c-.265,3.748-.681,4.355-.587,6.493a15.661,15.661,0,0,0,.587,3.607,46.124,46.124,0,0,0,5.866.722,24.7,24.7,0,0,0,9.385-.722,20.923,20.923,0,0,0,3.519-1.443"
                transform="translate(-70.047 -189.481)"
                {...p(32)}
              />
              {pct(32, "60%", "72%", "3%")}
            </g>
            {/* Back right foot */}
            <g>
              <path
                d="M163.889,498.005a31.4,31.4,0,0,1-19.771,1.262,55.2,55.2,0,0,1,1.695,6.311c.808,3.916,1.05,7.229,1.13,13.884.117,9.743.176,14.614-.565,20.2a89.624,89.624,0,0,1-2.26,11.36c-.528,1.058-1,2.012-1.412,2.84a7.114,7.114,0,0,0-.565,1.262,7.687,7.687,0,0,0-.565,3.155c.109,1.95,0,1.893.565,2.524,1.625,1.815,6.541,1.991,7.909,1.893,2.955-.211,5.828-.416,6.779-2.525a6.1,6.1,0,0,0,0-3.786c-.332-1.524-1.051-2.014-2.26-4.418a11.274,11.274,0,0,1-1.13-2.525,8,8,0,0,1,0-3.156,27.641,27.641,0,0,1,1.695-6.311c1.163-2.844,1.819-5.916,2.824-8.835,3.412-9.9,5.118-14.856,5.649-17.67a47.848,47.848,0,0,0,.282-15.462"
                transform="translate(-69.838 -227.205)"
                {...p(33)}
              />
              {pct(33, "60%", "84%", "3%")}
            </g>
          </svg>
        </div>

        {score ? (
          <div className="affected-skin-score-badge">
            <p className="affected-skin-score-title">{t("scoradScore.bsa")}</p>
            <p className="affected-skin-score-value">{score}%</p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default SelfStatsAffectedSkin;
